import React, { useState, useMemo, useCallback, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import { Button } from "@shared/ui/button";
import { Card, CardContent } from "@shared/ui/card";
import { Switch } from "@shared/ui/switch";
import { Input } from "@shared/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import {
  Pencil,
  Plus,
  Search,
  Calendar,
  User,
  Check,
  X,
  Clock,
  DollarSign,
  Loader2,
  AlertCircle,
} from "lucide-react";
import { useToast } from "@shared/hooks/use-toast";
import { Alert, AlertDescription } from "@shared/ui/alert";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import {
  useRates,
  useCreateRate,
  useUpdateRate,
  useToggleRateStatus,
} from "../../../../hooks";
import { Rate } from "../../../../types";
import { convertToSnakeCase } from "@shared/utils/caseConverter";
import { useCurrency } from "@shared/selectors/globalSelectors";

// ====================== TYPES ======================

interface RateFormData {
  rate: string;
  type: "normal" | "overtime";
  description?: string;
}

// ====================== HOOKS ======================

// Custom hook for debounced search
const useDebounce = (value: string, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  React.useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

// ====================== COMPONENTS ======================

// Empty state component
const EmptyState: React.FC<{
  hasSearchQuery: boolean;
  onAddNew: () => void;
}> = ({ hasSearchQuery, onAddNew }) => {
  return (
    <div className="col-span-full py-12 text-center text-muted-foreground bg-muted/20 rounded-lg border border-dashed">
      <div className="flex flex-col items-center gap-2">
        <Search className="h-10 w-10 text-muted-foreground/50" />
        <h4>No rates found</h4>
        <p className="text-sm">
          {hasSearchQuery
            ? "Try adjusting your search or add a new rate"
            : "No rates available"}
        </p>
        <Button variant="outline" className="mt-2" onClick={onAddNew}>
          <Plus className="mr-2 h-4 w-4" /> Add New Rate
        </Button>
      </div>
    </div>
  );
};

// ====================== MAIN COMPONENT ======================

const RatesTab: React.FC = () => {
  const currency = useCurrency();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [currentRate, setCurrentRate] = useState<Rate | null>(null);

  // Add state to track if we're in cancel mode
  const [isCanceling, setIsCanceling] = useState(false);

  // Get search query from URL
  const searchQuery = searchParams.get("search") || "";

  // Debounce the search input for better UX (but server handles the actual search)
  const [searchInput, setSearchInput] = useState(searchQuery);
  const debouncedSearchInput = useDebounce(searchInput, 500);

  // Update URL when debounced search changes
  useEffect(() => {
    const newSearchParams = new URLSearchParams(searchParams);
    if (debouncedSearchInput.trim()) {
      newSearchParams.set("search", debouncedSearchInput.trim());
    } else {
      newSearchParams.delete("search");
    }
    setSearchParams(newSearchParams, { replace: true });
  }, [debouncedSearchInput, searchParams, setSearchParams]);

  // API hooks - now using server-side search
  const { data: rates = [] } = useRates(searchQuery);
  const createRateMutation = useCreateRate();
  const updateRateMutation = useUpdateRate();
  const toggleStatusMutation = useToggleRateStatus();

  console.log("Ratee", rates);

  // Form fields configuration
  const formFields: FormField[] = useMemo(
    () => [
      {
        name: "type",
        type: "select",
        label: "Rate Type",
        placeholder: "Select rate type...",
        required: true,
        options: [
          { value: "normal", label: "Normal" },
          { value: "overtime", label: "Overtime" },
        ],
        helperText: "Choose whether this is a normal or overtime rate",
      },
      {
        name: "rate",
        type: "number",
        label: "Hourly Rate",
        placeholder: "25",
        required: true,
        min: 0,
        step: 0.01,
        prefix: <DollarSign className="h-4 w-4 text-muted-foreground" />,
        helperText: `Enter amount (e.g., 25 for ${currency} 25/hr)`,
        validate: (value: string) => {
          const numericRate = parseFloat(value);
          if (isNaN(numericRate) || numericRate <= 0) {
            return "Please enter a valid positive number for the rate";
          }
          return true;
        },
      },
      {
        name: "description",
        type: "textarea",
        label: "Description",
        placeholder: "Enter description...",
        rows: 3,
        maxLength: 500,
        className: "pt-2",
      },
    ],
    [],
  );

  // Create form instance using FormService - following CustomerInformationStep pattern
  const form = FormService.createForm({
    fields: formFields,
    mode: "onChange",
    validateOnMount: false,
    defaultValues: {
      rate: "",
      type: "normal",
      description: "",
    },
  });

  // Update form when editing a rate - but not during cancel
  useEffect(() => {
    if (isCanceling) return; // Skip form updates during cancel

    if (currentRate && isEditModalOpen) {
      const populateData = {
        rate: parseFloat(currentRate.rate.replace(/\$|\/hr/g, "")),
        type: currentRate.type,
        description: currentRate.description,
      };
      FormService.populateForm(form, populateData, formFields);
    } else if (isAddModalOpen) {
      const populateData = {
        rate: "",
        type: "",
        description: "",
      };
      FormService.populateForm(form, populateData, formFields);
    }
  }, [
    currentRate,
    isEditModalOpen,
    isAddModalOpen,
    form,
    formFields,
    isCanceling,
  ]);

  // Handle field changes - simplified like CustomerInformationStep
  const handleFieldChange = useCallback(
    (fieldName: string, value: any) => {
      // Prevent any field changes during cancel
      if (isCanceling) return;
    },
    [isCanceling],
  );

  // Handle form submission
  const handleSubmit = async (data: RateFormData) => {
    if (isCanceling) return; // Prevent submit during cancel

    try {
      if (currentRate) {
        const updatePayload = {
          id: currentRate.id,
          rate: data.rate,
          type: data.type,
          description: data.description,
        };
        // Update existing rate
        await updateRateMutation.mutateAsync(updatePayload);
        setIsEditModalOpen(false);
      } else {
        const createPayload = {
          rate: data.rate,
          type: data.type,
          description: data.description,
        };
        // Create new rate
        await createRateMutation.mutateAsync(createPayload);
        setIsAddModalOpen(false);
      }

      // Reset form state - following CustomerInformationStep pattern
      FormService.resetForm(form, formFields);
      setCurrentRate(null);
    } catch (error) {
      // Handle error
    }
  };

  const handleReset = useCallback(() => {
    if (isCanceling) return; // Prevent reset during cancel
    FormService.resetForm(form, formFields);
  }, [form, formFields, isCanceling]);

  // Event handlers
  const handleEditClick = useCallback((rate: Rate) => {
    setCurrentRate(rate);
    setIsEditModalOpen(true);
  }, []);

  const handleToggleActive = useCallback(
    async (rate: Rate) => {
      try {
        await toggleStatusMutation.mutateAsync({
          id: rate.id,
          isActive: !rate.isActive,
          name: rate.name,
        });
      } catch (error) {
        // Handle error
      }
    },
    [toggleStatusMutation],
  );

  // Handle modal close with cancel protection
  const handleAddModalClose = useCallback(() => {
    setIsCanceling(true);
    setIsAddModalOpen(false);
    setCurrentRate(null);

    // Reset form without triggering side effects
    setTimeout(() => {
      FormService.resetForm(form, formFields);
      setIsCanceling(false);
    }, 0);
  }, [form, formFields]);

  const handleEditModalClose = useCallback(() => {
    setIsCanceling(true);
    setIsEditModalOpen(false);
    setCurrentRate(null);

    // Reset form without triggering side effects
    setTimeout(() => {
      FormService.resetForm(form, formFields);
      setIsCanceling(false);
    }, 0);
  }, [form, formFields]);

  // Handle search input change
  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchInput(e.target.value);
    },
    [],
  );

  // Get rate type styling
  const getRateTypeStyle = (type: "normal" | "overtime") => {
    return type === "overtime"
      ? "text-orange-600 bg-orange-50 dark:bg-orange-950/30"
      : "text-blue-600 bg-blue-50 dark:bg-blue-950/30";
  };

  return (
    <div className="">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background pt-2 pb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-start flex-col">
          <h3 className="page-subheading">Rates</h3>
          <p className="page-subtitle">
            {" "}
            Set and manage employee hourly rates.
          </p>
        </div>
        <Button
          onClick={() => setIsAddModalOpen(true)}
          disabled={createRateMutation.isPending}
        >
          {createRateMutation.isPending ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Plus className="mr-2 h-4 w-4" />
          )}
          Add New Rate
        </Button>
      </div>

      {/* Grid */}
      <div className="relative pb-4 mt-2">
        <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search rates..."
          className="pl-8 w-full sm:w-[250px]"
          value={searchInput}
          onChange={handleSearchChange}
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {Array.isArray(rates) &&
          rates.map((rate) => (
            <Card
              key={rate.id}
              className="overflow-hidden transition-all hover:shadow-md hover:border-primary/20 group"
            >
              <CardContent className="p-0">
                <div className="p-4 border-b bg-muted/20">
                  <div className="flex justify-between items-start">
                    <div className="flex items-start gap-2">
                      <Clock className="h-5 w-5 text-green-600 mt-0.5" />
                      <div>
                        <h4 className="font-medium">{rate.name}</h4>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xl font-bold">
                            {currency} {rate.rate}
                          </span>
                          <span
                            className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${getRateTypeStyle(
                              rate.type,
                            )}`}
                          >
                            {rate.type === "normal" ? "Normal" : "Overtime"}
                          </span>
                        </div>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEditClick(rate)}
                      className="opacity-0 group-hover:opacity-100 hover:bg-background/80"
                      disabled={updateRateMutation.isPending}
                    >
                      {updateRateMutation.isPending &&
                      currentRate?.id === rate.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Pencil className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>

                <div className="p-4">
                  <div className="flex gap-2 items-center text-sm text-muted-foreground mb-2">
                    <User className="h-3.5 w-3.5" />
                    <p>{rate.createdBy}</p>
                  </div>
                  <div className="flex gap-2 items-center text-sm text-muted-foreground mb-4">
                    <Calendar className="h-3.5 w-3.5" />
                    <p>{rate.createdDate}</p>
                  </div>

                  <div className="flex items-center justify-between pt-4 border-t">
                    <span className="text-sm">
                      {rate.isActive ? (
                        <span className="flex items-center gap-1.5 text-green-600 bg-green-50 dark:bg-green-950/30 px-2 py-1 rounded-full text-xs font-medium">
                          <Check className="h-3 w-3" /> Active
                        </span>
                      ) : (
                        <span className="flex items-center gap-1.5 text-gray-600 bg-gray-50 dark:bg-gray-800/30 px-2 py-1 rounded-full text-xs font-medium">
                          <X className="h-3 w-3" /> Inactive
                        </span>
                      )}
                    </span>
                    <Switch
                      checked={rate.isActive}
                      onCheckedChange={() => handleToggleActive(rate)}
                      disabled={toggleStatusMutation.isPending}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}

        {Array.isArray(rates) && rates.length === 0 && (
          <EmptyState
            hasSearchQuery={!!searchQuery}
            onAddNew={() => setIsAddModalOpen(true)}
          />
        )}
      </div>

      {/* Add Modal */}
      <Dialog open={isAddModalOpen} onOpenChange={handleAddModalClose}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Add New Rate</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 pr-4">
            <DynamicForm
              fields={formFields}
              form={form}
              onSubmit={handleSubmit}
              onReset={handleReset}
              onFieldChange={handleFieldChange}
              loading={createRateMutation.isPending}
              gridColumns={1}
              responsiveColumns={{
                sm: 1,
                md: 1,
              }}
              config={{
                showResetButton: false,
                showSubmitButton: false,
                resetOnSubmit: false,
                classes: {
                  form: "space-y-4",
                  container: "space-y-2",
                  label: "block text-sm font-medium text-gray-900 mb-1",
                  input:
                    "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed",
                  select:
                    "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white disabled:bg-gray-100 disabled:cursor-not-allowed",
                  error: "text-sm text-red-600 mt-1",
                  helperText: "text-xs text-muted-foreground mt-1",
                  required: "text-red-500 ml-1",
                },
              }}
              renderCustomFooter={() => (
                <div className="flex justify-end gap-2 pt-4">
                  <Button
                    variant="outline"
                    onClick={handleAddModalClose}
                    disabled={createRateMutation.isPending}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={form.handleSubmit(handleSubmit)}
                    disabled={createRateMutation.isPending}
                  >
                    {createRateMutation.isPending ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      "Save Rate"
                    )}
                  </Button>
                </div>
              )}
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={isEditModalOpen} onOpenChange={handleEditModalClose}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Edit Rate</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 pr-4">
            <DynamicForm
              fields={formFields}
              form={form}
              onSubmit={handleSubmit}
              onReset={handleReset}
              onFieldChange={handleFieldChange}
              loading={updateRateMutation.isPending}
              gridColumns={2}
              responsiveColumns={{
                sm: 1,
                md: 2,
              }}
              config={{
                showResetButton: false,
                showSubmitButton: false,
                resetOnSubmit: false,
                classes: {
                  form: "space-y-4",
                  container: "space-y-2",
                  label: "block text-sm font-medium text-gray-900 mb-1",
                  input:
                    "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed",
                  textarea:
                    "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-vertical disabled:bg-gray-100 disabled:cursor-not-allowed",
                  select:
                    "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white disabled:bg-gray-100 disabled:cursor-not-allowed",
                  error: "text-sm text-red-600 mt-1",
                  helperText: "text-xs text-muted-foreground mt-1",
                  required: "text-red-500 ml-1",
                },
              }}
              renderCustomFooter={() => (
                <div className="flex justify-end gap-2 pt-4">
                  <Button
                    variant="outline"
                    onClick={handleEditModalClose}
                    disabled={updateRateMutation.isPending}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={form.handleSubmit(handleSubmit)}
                    disabled={updateRateMutation.isPending}
                  >
                    {updateRateMutation.isPending ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Updating...
                      </>
                    ) : (
                      "Update Rate"
                    )}
                  </Button>
                </div>
              )}
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RatesTab;
